/**
 * Algeria wilaya adjacency graph — used to suggest the geographically nearest
 * league group for an incoming team whose own wilaya isn't represented in any
 * group yet (relegations from a higher league / promotions from regionals).
 *
 * Keys are human-readable wilaya names; lookups go through `normalizeWilaya`
 * so the messy real-world spellings stored on teams ("MEDEA", "Béjaïa",
 * "M'sila", "ain temouchent", "El bayadh") all resolve to the same node.
 *
 * The list is intentionally land-border based (shared frontier = neighbour).
 * It does not need to be exhaustive to be useful: the suggestion algorithm
 * does a BFS, so two-hop distances are derived automatically. Extend freely
 * as new leagues onboard.
 */

export function normalizeWilaya(value: string | null | undefined): string {
  if (!value) return "";
  return value
    .toString()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "") // strip diacritics
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "") // drop spaces, apostrophes, hyphens
    .trim();
}

// Readable adjacency. Each entry lists the wilayas sharing a land border.
const RAW_ADJACENCY: Record<string, string[]> = {
  // ── North-Centre ──────────────────────────────────────────────
  Alger: ["Blida", "Tipaza", "Boumerdès"],
  Blida: ["Alger", "Tipaza", "Aïn Defla", "Médéa", "Bouira", "Boumerdès"],
  Tipaza: ["Alger", "Blida", "Aïn Defla", "Chlef"],
  Boumerdès: ["Alger", "Blida", "Bouira", "Tizi Ouzou"],
  "Tizi Ouzou": ["Boumerdès", "Bouira", "Béjaïa"],
  Bouira: ["Blida", "Boumerdès", "Tizi Ouzou", "Béjaïa", "Bordj Bou Arreridj", "M'sila", "Médéa"],
  Médéa: ["Blida", "Aïn Defla", "Djelfa", "M'sila", "Bouira", "Tissemsilt"],
  "Aïn Defla": ["Tipaza", "Blida", "Médéa", "Chlef", "Tissemsilt", "Tiaret"],
  Chlef: ["Tipaza", "Aïn Defla", "Tissemsilt", "Relizane", "Mostaganem", "Tiaret"],
  Djelfa: ["Médéa", "M'sila", "Laghouat", "Tiaret", "Ghardaïa", "Biskra"],

  // ── North-East ────────────────────────────────────────────────
  Béjaïa: ["Tizi Ouzou", "Bouira", "Bordj Bou Arreridj", "Sétif", "Jijel"],
  Jijel: ["Béjaïa", "Sétif", "Mila", "Constantine", "Skikda"],
  Sétif: ["Béjaïa", "Bordj Bou Arreridj", "M'sila", "Batna", "Mila", "Jijel"],
  "Bordj Bou Arreridj": ["Béjaïa", "Bouira", "M'sila", "Sétif"],
  "M'sila": ["Bouira", "Bordj Bou Arreridj", "Sétif", "Batna", "Biskra", "Djelfa", "Médéa"],
  Batna: ["Sétif", "M'sila", "Biskra", "Khenchela", "Oum El Bouaghi", "Mila"],
  Mila: ["Sétif", "Jijel", "Constantine", "Oum El Bouaghi", "Batna", "Skikda"],
  Constantine: ["Mila", "Jijel", "Skikda", "Guelma", "Oum El Bouaghi"],
  Skikda: ["Jijel", "Constantine", "Guelma", "Annaba", "Mila"],
  Guelma: ["Constantine", "Skikda", "Annaba", "Souk Ahras", "Oum El Bouaghi"],
  Annaba: ["Skikda", "Guelma", "El Tarf", "Souk Ahras"],
  "El Tarf": ["Annaba", "Souk Ahras"],
  "Souk Ahras": ["Annaba", "Guelma", "Oum El Bouaghi", "Tébessa", "El Tarf"],
  "Oum El Bouaghi": ["Constantine", "Guelma", "Souk Ahras", "Tébessa", "Khenchela", "Batna", "Mila"],
  Khenchela: ["Batna", "Oum El Bouaghi", "Tébessa", "Biskra"],
  Tébessa: ["Souk Ahras", "Oum El Bouaghi", "Khenchela", "Biskra"],

  // ── North-West ────────────────────────────────────────────────
  Oran: ["Aïn Témouchent", "Mascara", "Mostaganem", "Sidi Bel Abbès"],
  Mostaganem: ["Oran", "Mascara", "Relizane", "Chlef"],
  Relizane: ["Mostaganem", "Chlef", "Tiaret", "Mascara"],
  Mascara: ["Oran", "Mostaganem", "Relizane", "Tiaret", "Saïda", "Sidi Bel Abbès"],
  "Sidi Bel Abbès": ["Oran", "Aïn Témouchent", "Mascara", "Saïda", "Tlemcen", "El Bayadh"],
  "Aïn Témouchent": ["Oran", "Sidi Bel Abbès", "Tlemcen"],
  Tlemcen: ["Aïn Témouchent", "Sidi Bel Abbès", "Naâma"],
  Tiaret: ["Aïn Defla", "Chlef", "Relizane", "Mascara", "Saïda", "Laghouat", "Djelfa", "Tissemsilt", "El Bayadh"],
  Tissemsilt: ["Chlef", "Aïn Defla", "Médéa", "Tiaret"],
  Saïda: ["Mascara", "Sidi Bel Abbès", "Tiaret", "El Bayadh", "Naâma"],

  // ── High Plateaus / Sud ───────────────────────────────────────
  Laghouat: ["Djelfa", "Tiaret", "El Bayadh", "Ghardaïa", "Biskra"],
  "El Bayadh": ["Saïda", "Sidi Bel Abbès", "Tiaret", "Laghouat", "Naâma", "Béchar", "Ghardaïa", "Adrar"],
  Naâma: ["Tlemcen", "Saïda", "El Bayadh", "Béchar"],
  Biskra: ["Batna", "M'sila", "Djelfa", "Khenchela", "Tébessa", "Ouled Djellal", "El Oued"],
  Ghardaïa: ["Laghouat", "Djelfa", "El Bayadh", "Ouargla", "Adrar", "El M'Ghair", "Ouled Djellal", "Ménéa"],

  // ── Deep South ────────────────────────────────────────────────
  Béchar: ["Naâma", "El Bayadh", "Adrar", "Béni Abbès", "Tindouf"],
  Adrar: ["Béchar", "El Bayadh", "Ghardaïa", "Tindouf", "Tamanrasset", "Béni Abbès", "Timimoun", "Bordj Badji Mokhtar"],
  Tindouf: ["Béchar", "Adrar"],
  Ouargla: ["Ghardaïa", "El Oued", "Touggourt", "Illizi", "El M'Ghair"],
  "El Oued": ["Biskra", "Ouargla", "Touggourt", "El M'Ghair", "Ouled Djellal"],
  Touggourt: ["Ouargla", "El Oued", "El M'Ghair", "Ghardaïa"],
  "El M'Ghair": ["El Oued", "Touggourt", "Ouargla", "Ghardaïa", "Biskra", "Ouled Djellal"],
  "Ouled Djellal": ["Biskra", "El Oued", "El M'Ghair", "Ghardaïa"],
  Illizi: ["Ouargla", "Tamanrasset", "Djanet", "In Amenas"],
  "In Amenas": ["Illizi", "Djanet"],
  Djanet: ["Illizi", "Tamanrasset", "In Amenas"],
  Ménéa: ["Ghardaïa", "Adrar", "Timimoun"],
  Timimoun: ["Adrar", "Ghardaïa", "Béni Abbès", "Ménéa"],
  "Béni Abbès": ["Béchar", "Adrar", "Timimoun"],
  Tamanrasset: ["Adrar", "Illizi", "Djanet", "In Salah", "In Guezzam", "Bordj Badji Mokhtar"],
  "In Salah": ["Tamanrasset", "Ghardaïa", "Adrar"],
  "In Guezzam": ["Tamanrasset"],
  "Bordj Badji Mokhtar": ["Adrar", "Tamanrasset"],
};

/**
 * Normalized adjacency: `{ normalizedWilaya: Set<normalizedNeighbour> }`.
 * Built once, symmetric (if A lists B, B gets A even if the raw map missed it).
 */
export const WILAYA_ADJACENCY: Record<string, Set<string>> = (() => {
  const map: Record<string, Set<string>> = {};
  const add = (a: string, b: string) => {
    const na = normalizeWilaya(a);
    const nb = normalizeWilaya(b);
    if (!na || !nb || na === nb) return;
    (map[na] ??= new Set()).add(nb);
    (map[nb] ??= new Set()).add(na);
  };
  for (const [w, neighbours] of Object.entries(RAW_ADJACENCY)) {
    map[normalizeWilaya(w)] ??= new Set();
    for (const n of neighbours) add(w, n);
  }
  return map;
})();

/**
 * BFS graph distance between two wilayas (0 = same, 1 = neighbour, …).
 * Returns Infinity if unreachable. Inputs are normalized internally.
 */
export function wilayaDistance(from: string, to: string): number {
  const a = normalizeWilaya(from);
  const b = normalizeWilaya(to);
  if (!a || !b) return Infinity;
  if (a === b) return 0;
  const visited = new Set<string>([a]);
  let frontier = [a];
  let dist = 0;
  while (frontier.length) {
    dist += 1;
    const next: string[] = [];
    for (const node of frontier) {
      for (const nb of WILAYA_ADJACENCY[node] ?? []) {
        if (nb === b) return dist;
        if (!visited.has(nb)) {
          visited.add(nb);
          next.push(nb);
        }
      }
    }
    frontier = next;
    if (dist > 12) break; // safety; Algeria diameter is well under this
  }
  return Infinity;
}
